import React from 'react';
import { Box, Button } from '@mui/material';
import { AppDataGrid } from '@/components/ui';
import { AppCard } from '@/components/app-card/app-card';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { COLORS } from '@/theme/colors';
import { GridColDef } from '@mui/x-data-grid';
import { Department } from '../hooks/useDepartments';
import {
  getEffectiveBudgetValue,
  getEffectiveBudgetCurrency,
  calculateSharePercentage,
} from '../../calculations';
import { PendingBudgetChange } from '../../types';
import { formatCurrencyNoUnit } from '@/lib/formatters';

interface DepartmentsTableProps {
  departmentsList: Department[];
  pendingBudgetChanges: Map<number, PendingBudgetChange>;
  globalBudgetValue: number;
  globalBudgetCurrency?: string;
  onEditDepartment: (department: Department) => void;
  onSetBudget: (department: Department) => void;
  onCreateDepartment: () => void;
  onBulkSave: () => void;
  onCancelChanges: () => void;
  bulkSaveLoading: boolean;
  canCreateDepartment: boolean;
  canEditDepartment: boolean;
  canSetBudget: boolean;
}

export const DepartmentsTable: React.FC<DepartmentsTableProps> = ({
  departmentsList,
  pendingBudgetChanges,
  globalBudgetValue,
  globalBudgetCurrency,
  onEditDepartment,
  onSetBudget,
  onCreateDepartment,
  onBulkSave,
  onCancelChanges,
  bulkSaveLoading,
  canCreateDepartment,
  canEditDepartment,
  canSetBudget,
}) => {
  const columns: GridColDef<Department>[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'username', headerName: 'Department Owner', flex: 1 },
    {
      field: 'value',
      headerName: 'Annual Department Budget',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: any) => {
        const effectiveValue = getEffectiveBudgetValue(
          params.row,
          pendingBudgetChanges,
        );
        const effectiveCurrency = getEffectiveBudgetCurrency(
          params.row,
          pendingBudgetChanges,
        );
        const hasPendingChange = pendingBudgetChanges.has(params.row.id);

        if (!effectiveValue && !params.row.value) {
          return <Box sx={{ color: '#E23D3D' }}>No budget set</Box>;
        }

        return (
          <Box
            sx={{
              color: hasPendingChange ? COLORS.primary : 'inherit',
              fontWeight: hasPendingChange ? 'bold' : 'normal',
            }}>
            {effectiveValue !== undefined ? formatCurrencyNoUnit(Number(effectiveValue)): '0.00'} {effectiveCurrency}
            {hasPendingChange && ' *'}
          </Box>
        );
      },
    },
    {
      field: 'share',
      headerName: 'Share in Global Budget',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: any) => {
        const effectiveCurrency = getEffectiveBudgetCurrency(
          params.row,
          pendingBudgetChanges,
        );

        // Hide share if currency is different from global budget currency
        if (
          globalBudgetCurrency &&
          effectiveCurrency &&
          effectiveCurrency !== globalBudgetCurrency
        ) {
          return (
            <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              Different currency
            </Box>
          );
        }

        const sharePercentage = calculateSharePercentage(
          params.row,
          globalBudgetValue,
          pendingBudgetChanges,
        );
        const hasPendingChange = pendingBudgetChanges.has(params.row.id);

        return (
          <Box
            sx={{
              color: hasPendingChange ? COLORS.primary : 'inherit',
              fontWeight: hasPendingChange ? 'bold' : 'normal',
            }}>
            {sharePercentage}
            {hasPendingChange && ' *'}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.2,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEditDepartment && (
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEditDepartment(params.row);
              }}
              data-testid="department-row-edit">
              Edit
            </Button>
          )}
          {canSetBudget && (
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onSetBudget(params.row);
              }}>
              {params.row.value && !pendingBudgetChanges.has(params.row.id)
                ? 'Change Budget'
                : 'Set Budget'}
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <AppCard
      className="card100"
      title="Departments List"
      icon={<BarChartOutlinedIcon color="primary" />}
      attributes={{ 'data-testid': 'departments-list-card' } as any}
      headerRight={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {canCreateDepartment && (
            <Button
              variant="contained"
              onClick={onCreateDepartment}
              color="primary"
              data-testid="departments-add">
              Add
            </Button>
          )}
        </Box>
      }
      footerRight={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {pendingBudgetChanges.size > 0 && (
            <Button
              variant="outlined"
              onClick={onCancelChanges}
              color="inherit"
              style={{
                marginTop: '10px',
                marginBottom: '10px',
              }}>
              Cancel Changes ({pendingBudgetChanges.size})
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onBulkSave}
            color="primary"
            disabled={pendingBudgetChanges.size === 0 || bulkSaveLoading}
            style={{
              float: 'right',
              marginTop: '10px',
              marginBottom: '10px',
            }}
            data-testid="departments-set-budgets">
            {bulkSaveLoading
              ? 'Saving...'
              : `Set Budgets${
                  pendingBudgetChanges.size > 0
                    ? ` (${pendingBudgetChanges.size})`
                    : ''
                }`}
          </Button>
        </Box>
      }>
      <Box data-testid="departments-grid">
        <AppDataGrid
          style={{ height: '450px', marginLeft: '0px' }}
          rows={departmentsList}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[]}
          hideFooterSelectedRowCount={true}
          slotProps={{
            pagination: {
              labelRowsPerPage: '',
            } as any,
          }}
        />
      </Box>
    </AppCard>
  );
};
